import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Button, IconButton, Drawer, List,
  ListItem, ListItemText, useTheme, useMediaQuery, Box, Container,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const MotionBox = motion(Box);

const navItems = [
  { text: 'Home',     path: '/' },
  { text: 'Projects', path: '/projects' },
  { text: 'Skills',   path: '/skills' },
  { text: 'Blog',     path: '/blog' },
  { text: 'Contact',  path: '/contact' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled,   setScrolled]   = useState(false);
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();

  useEffect(() => {
    const handle = () => setScrolled(window.scrollY > 1);
    window.addEventListener('scroll', handle);
    return () => window.removeEventListener('scroll', handle);
  }, []);

  const drawer = (
    <List sx={{
      height: '100%',
      background: 'rgba(0,0,0,0.92)',
      backdropFilter: 'saturate(180%) blur(24px)',
      WebkitBackdropFilter: 'saturate(180%) blur(24px)',
      pt: 4,
    }}>
      {navItems.map((item) => {
        const isActive = location.pathname === item.path;
        return (
          <ListItem
            key={item.text}
            component={RouterLink}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            sx={{
              py: 1.8,
              color: isActive ? '#2997FF' : 'rgba(245,245,247,0.75)',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              transition: 'color 0.18s ease',
              '&:hover': { color: '#F5F5F7', background: 'transparent' },
            }}
          >
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{ fontSize: '1.05rem', fontWeight: isActive ? 600 : 400, letterSpacing: '-0.01em' }}
            />
          </ListItem>
        );
      })}
    </List>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: scrolled ? 'rgba(0,0,0,0.6)' : 'transparent',
          backdropFilter: scrolled ? 'saturate(180%) blur(20px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'saturate(180%) blur(20px)' : 'none',
          transition: 'background 0.35s ease, backdrop-filter 0.35s ease',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.13) 50%, transparent 100%)',
            opacity: scrolled ? 1 : 0,
            transition: 'opacity 0.35s ease',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: 64 }}>

            {/* Logo */}
            <MotionBox
              component={RouterLink}
              to="/"
              sx={{ textDecoration: 'none', mr: 5, display: 'flex', alignItems: 'center' }}
              whileHover={{ opacity: 0.75 }}
              whileTap={{ scale: 0.96 }}
              transition={{ duration: 0.15 }}
            >
              <Box sx={{
                fontSize: '1.15rem',
                fontWeight: 700,
                color: '#F5F5F7',
                letterSpacing: '-0.03em',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                fontFamily: '"Bricolage Grotesque", -apple-system, BlinkMacSystemFont, sans-serif',
              }}>
                Michael Yarmoshik
              </Box>
            </MotionBox>

            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop nav */}
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 0, alignItems: 'center' }}>
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Button
                      key={item.text}
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        color: isActive ? '#F5F5F7' : 'rgba(245,245,247,0.5)',
                        fontSize: '0.86rem',
                        fontWeight: isActive ? 500 : 400,
                        px: 1.8,
                        letterSpacing: '-0.01em',
                        background: 'none',
                        borderRadius: '8px',
                        transition: 'color 0.18s ease',
                        '&:hover': {
                          color: '#F5F5F7',
                          background: 'transparent',
                        },
                      }}
                    >
                      {item.text}
                    </Button>
                  );
                })}

              </Box>
            )}

            {/* Mobile burger */}
            {isMobile && (
              <IconButton
                onClick={() => setMobileOpen((v) => !v)}
                sx={{ color: 'rgba(245,245,247,0.75)', '&:hover': { color: '#F5F5F7', background: 'rgba(255,255,255,0.08)' }, borderRadius: '8px' }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {mobileOpen
                    ? <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }}><CloseIcon /></motion.div>
                    : <motion.div key="m" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.18 }}><MenuIcon /></motion.div>
                  }
                </AnimatePresence>
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { width: '100%', background: 'transparent', boxSizing: 'border-box' },
        }}
      >
        <AnimatePresence>
          {mobileOpen && (
            <MotionBox
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.26 }}
            >
              {drawer}
            </MotionBox>
          )}
        </AnimatePresence>
      </Drawer>

      <Box sx={{ height: 64 }} />
    </>
  );
}
